import { api } from './api.ts';
import { cipher } from './session.ts';
import type { DaoApiResponse } from '@/types/dao.ts';
import type {
  WithdrawAccount,
  WithdrawBank,
  WithdrawInfo,
  WithdrawItem,
} from '@/types/withdraw.ts';

/**
 * 포인트 출금 API.
 *
 * 백엔드는 엔드포인트 하나(`/api/user/withdraw.json`)에 `action` 으로 갈린다.
 * 라우터가 파일명을 `[-_,.]` 로 쪼개 메서드명을 만들기 때문에 `withdraw.request.json`
 * 같은 주소는 메서드명이 엉킨다 — 그래서 이 모양이다.
 */

/** 출금 화면에 필요한 값 일체 — 잔액·정책·계좌·진행중 신청 */
export async function fetchWithdrawInfo(userSeq: number): Promise<DaoApiResponse<WithdrawInfo>> {
  const encrypted = await cipher.encryptJson({ userSeq, action: 'info' });
  return api.post<DaoApiResponse<WithdrawInfo>>('/api/user/withdraw.json', encrypted);
}

/**
 * 은행 목록.
 *
 * ★ 화면에 목록을 박아 두지 않는다. 코드는 이 서비스의 **내부 코드**(국민=3)라
 *   표준 은행코드(국민=004)와 다르다. 서버가 주는 값만 계좌 등록에 쓸 수 있다.
 */
export async function fetchBanks(
  userSeq: number
): Promise<DaoApiResponse<{ banks: WithdrawBank[] }>> {
  const encrypted = await cipher.encryptJson({ userSeq, action: 'banks' });
  return api.post<DaoApiResponse<{ banks: WithdrawBank[] }>>('/api/user/withdraw.json', encrypted);
}

/** 출금 계좌 등록·변경. 은행 이름은 서버가 코드로 채운다. */
export async function saveWithdrawAccount(
  userSeq: number,
  bankCode: string,
  account: string,
  holder: string
): Promise<DaoApiResponse<{ account: WithdrawAccount }>> {
  const encrypted = await cipher.encryptJson({
    userSeq,
    bankCode,
    account,
    holder,
    action: 'account',
  });
  return api.post<DaoApiResponse<{ account: WithdrawAccount }>>(
    '/api/user/withdraw.json',
    encrypted
  );
}

/**
 * 출금 신청.
 *
 * 신청하는 순간 포인트가 **바로 차감된다.** 반려되면 서버가 되돌린다.
 */
export async function requestWithdraw(
  userSeq: number,
  point: number
): Promise<DaoApiResponse<{ withdraw: WithdrawItem; balance: number }>> {
  const encrypted = await cipher.encryptJson({ userSeq, point, action: 'request' });
  return api.post<DaoApiResponse<{ withdraw: WithdrawItem; balance: number }>>(
    '/api/user/withdraw.json',
    encrypted
  );
}

/** 내 신청 취소. 처리 전에만 된다. */
export async function cancelWithdraw(
  userSeq: number,
  withdrawSeq: number
): Promise<DaoApiResponse<{ withdraw: WithdrawItem; balance: number }>> {
  const encrypted = await cipher.encryptJson({ userSeq, withdrawSeq, action: 'cancel' });
  return api.post<DaoApiResponse<{ withdraw: WithdrawItem; balance: number }>>(
    '/api/user/withdraw.json',
    encrypted
  );
}
